import { updateFlareNodeAttrs } from '@common/flare/prosemirror/transform/node';
import { getTrackedChangesState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Attrs, Node, NodeType } from 'prosemirror-model';
import { EditorState, NodeSelection, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

export type InsertInlineNodeCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Attrs) => boolean;

export function insertInlineNode(nodeType: NodeType, getAttrs?: (attrs: Attrs) => Attrs): InsertInlineNodeCommand {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Attrs): boolean {
    const selection = state.selection;
    const tr = state.tr;
    const $from = selection.$from;
    const index = $from.index();

    // Node must be an inline node and the selection needs to be replaced by the inline or the block node selection can be replaced by the default block with the inline node as the child
    if (!nodeType.isInline || !($from.parent.canReplaceWith(index, index, nodeType) || (selection instanceof NodeSelection && (selection as NodeSelection).node.isBlock))) {
      return false;
    }

    if (dispatch) {
      const trackedChangesState = getTrackedChangesState(state);
      const newAttrs = typeof getAttrs === 'function' ? getAttrs(attrs) : attrs;

      // If the selection is the inline node then update the attributes instead of replacing the node
      if (selection instanceof NodeSelection && selection.node.type === nodeType) {
        updateFlareNodeAttrs(tr, selection.node, $from.pos, newAttrs, trackedChangesState);
        tr.setSelection(NodeSelection.create(tr.doc, $from.pos));
      } else {
        if (trackedChangesState?.enabled) {
          // Insert a tracked change removal
          if (!selection.empty) {
            tr.replaceSelection(
              copySliceWithChanges(trackedChangesState.changeMarkType, state.selection.content(), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove))
            );
          }

          // Create a tracked add for the new node
          const addChangeAttrs = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add, { changeId: trackedChangesState.getParentChangeId($from, ChangeType.Add) });
          let addedNode: Node = nodeType.create({
            ...newAttrs,
            ...addChangeAttrs
          });

          // Wrap the inline node in a block node if needed
          if (selection instanceof NodeSelection && selection.node.isBlock) {
            const wrappedType = $from.parent.type.contentMatch.defaultType;
            // If the wrapped type doesn't allow inline content, wrap the inline node in an mcCentralContainer
            if (!wrappedType.inlineContent) {
              addedNode = state.schema.nodes.mcCentralContainer.create(addChangeAttrs, addedNode);
            }
            addedNode = wrappedType.create(addChangeAttrs, addedNode);
          }

          // Insert the new node
          tr.insert($from.pos, addedNode);

          // Set the selection to the end of the inserted node
          tr.setSelection(TextSelection.near(tr.doc.resolve($from.pos + addedNode.nodeSize), -1));
        } else {
          tr.replaceSelectionWith(nodeType.create(newAttrs));
        }
      }

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        tr.setMeta(TrackedChangesTrackedMetaKey, true);
      }

      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}
